import { getSandboxUrl } from "./sandboxUrl";
import { retrieveAuthToken } from "../utils";

import {
  ApiTokensClient,
  AssignmentsClient,
  AuthClient,
  ChoresClient,
  DirectoryClient,
  IdpClient,
  ProctorClient,
  ReportClient,
} from "@securityjourney/frontend-lib-svc";
export type { UserProfile } from "@securityjourney/frontend-lib-svc";

export type { CodeSubHintResponse, AiHint } from "@securityjourney/svc-learner-ai";

// Export some global types
export { ServiceError } from "@securityjourney/service-client";

// Clients from imported libraries
import { SandboxClient } from "@securityjourney/svc-sandbox";
export type {
  SandboxStatus,
  SandboxCode,
  TestStatus,
  SandboxCredentials,
} from "@securityjourney/svc-sandbox";

import { LessonEventsClient } from "@securityjourney/svc-lesson";
export type {
  LessonEventsClient,
  HintUsageResponse,
  CtfSubmissionResponse,
} from "@securityjourney/svc-lesson";

import { ScormClient } from "@securityjourney/svc-scorm";

import { HintsClient, HintsConfigureClient } from "@securityjourney/svc-learner-ai";
export type { HintsClient, HintsConfigureClient } from "@securityjourney/svc-learner-ai";

// Client written in the toolkit, but with types from imported services
import { LessonContentClient } from "./svcContentClient";
export type {
  LessonInstructions,
  LessonDetails,
  TestCase,
  TestConfiguration,
  ReplParam,
} from "./svcContentClient";
import { CodeSubClient } from "./svcCodeSubClient";
export type {
  CodeSubmission,
  CodeSubClient,
  CodeSubTestResult,
  CodeSubVolume,
} from "./svcCodeSubClient";
import { IntegrationsClient } from "./svcIntegrationsClient";
import { ExperimentClient } from "./svcExperimentClient";
export type { CWE, CWEsResponse, IntegrationsClient } from "./svcIntegrationsClient";

// Singleton instances of each client
let apiTokensClientInstance: ApiTokensClient | null = null;
let assignmentsClientsInstance: AssignmentsClient | null = null;
let authClientInstance: AuthClient | null = null;
let choreClientInstance: ChoresClient | null = null;
let directoryClientInstance: DirectoryClient | null = null;
let idpClientInstance: IdpClient | null = null;
let lessonContentClientInstance: LessonContentClient | null = null;
let proctorClientInstance: ProctorClient | null = null;
let reportClientInstance: ReportClient | null = null;
let hintsConfigureClientInstance: HintsConfigureClient | null = null;
let sandboxClientInstance: SandboxClient | null = null;
let integrationsClientInstance: IntegrationsClient | null = null;
let codeSubClientInstance: CodeSubClient | null = null;
let hintsClientInstance: HintsClient | null = null;
let scormClientInstance: ScormClient | null = null;
let lessonEventsClientInstance: LessonEventsClient | null = null;
let experimentClientInstance: ExperimentClient | null = null;

// Exported functions to get singleton instances of each client
export const getLessonEventsClient = (): LessonEventsClient => {
  if (!lessonEventsClientInstance) {
    lessonEventsClientInstance = new LessonEventsClient(retrieveAuthToken);
  }
  return lessonEventsClientInstance;
};

export const getHintsClient = (): HintsClient => {
  if (!hintsClientInstance) {
    hintsClientInstance = new HintsClient(retrieveAuthToken);
  }
  return hintsClientInstance;
};

export const getScormClient = (): ScormClient => {
  if (!scormClientInstance) {
    scormClientInstance = new ScormClient({});
  }
  return scormClientInstance;
};

export const getApiTokensClient = (): ApiTokensClient => {
  if (!apiTokensClientInstance) {
    apiTokensClientInstance = new ApiTokensClient(retrieveAuthToken);
  }
  return apiTokensClientInstance;
};

export const getAssignmentsClient = (): AssignmentsClient => {
  if (!assignmentsClientsInstance) {
    assignmentsClientsInstance = new AssignmentsClient();
  }
  return assignmentsClientsInstance;
};

export const getAuthClient = (): AuthClient => {
  if (!authClientInstance) {
    authClientInstance = new AuthClient();
  }
  return authClientInstance;
};

export const getChoresClient = (): ChoresClient => {
  if (!choreClientInstance) {
    choreClientInstance = new ChoresClient(retrieveAuthToken);
  }
  return choreClientInstance;
};

export const getDirectoryClient = (): DirectoryClient => {
  if (!directoryClientInstance) {
    directoryClientInstance = new DirectoryClient(retrieveAuthToken);
  }
  return directoryClientInstance;
};

export const getIdpClient = (): IdpClient => {
  if (!idpClientInstance) {
    idpClientInstance = new IdpClient(retrieveAuthToken);
  }
  return idpClientInstance;
};

export const getLessonContentClient = (): LessonContentClient => {
  if (!lessonContentClientInstance) {
    lessonContentClientInstance = new LessonContentClient(retrieveAuthToken);
  }
  return lessonContentClientInstance;
};

export const getProctorClient = (): ProctorClient => {
  if (!proctorClientInstance) {
    proctorClientInstance = new ProctorClient(retrieveAuthToken);
  }
  return proctorClientInstance;
};

export const getReportClient = (): ReportClient => {
  if (!reportClientInstance) {
    reportClientInstance = new ReportClient(retrieveAuthToken);
  }
  return reportClientInstance;
};

export const getHintsConfigureClient = (): HintsConfigureClient => {
  if (!hintsConfigureClientInstance) {
    hintsConfigureClientInstance = new HintsConfigureClient(retrieveAuthToken);
  }
  return hintsConfigureClientInstance;
};

export const getSandboxClient = (): SandboxClient => {
  const sandboxUrl = getSandboxUrl();
  if (!sandboxClientInstance) {
    sandboxClientInstance = new SandboxClient(retrieveAuthToken, sandboxUrl);
  }
  return sandboxClientInstance;
};

export const getIntegrationsClient = (): IntegrationsClient => {
  if (!integrationsClientInstance) {
    integrationsClientInstance = new IntegrationsClient(retrieveAuthToken);
  }
  return integrationsClientInstance;
};

export const getCodeSubClient = (): CodeSubClient => {
  if (!codeSubClientInstance) {
    codeSubClientInstance = new CodeSubClient(retrieveAuthToken);
  }
  return codeSubClientInstance;
};

export const getExperimentClient = (): ExperimentClient => {
  if (!experimentClientInstance) {
    experimentClientInstance = new ExperimentClient(retrieveAuthToken);
  }
  return experimentClientInstance;
};
